import {
	ApiError,
	AppError,
	AppMimeType,
	Currency,
	ErrorCode,
	Language,
} from '@/types/types';
import { formatDistance, Locale } from 'date-fns';
import { flatten } from 'lodash';
import { isAxiosError } from 'axios';
import { DateValue } from '@mantine/dates';
import { ComplaintStatus } from '@/api/ComplaintApi';
import { UserStatus } from '@/types/user';
import { MIME_TYPES } from '@mantine/dropzone';
import { OrderStatus } from '@/api/OrderApi';
import { DimensionsConfig } from '@/api/ProductTypeApi';
import { CreatorState, DimensionObject } from '@/types/creator';
import { ProductAttribute, ProductDimension } from '@/api/ProductApi';
import { AttributeViewType } from '@/api/AttributeApi';
import { DiscountType } from '@/api/OfferApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForInput(date: moment.MomentInput, format?: string) {
	return moment(date, format).format('YYYY-MM-DD');
}

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return PHONE_REGEX.test(phoneNumber) && phoneNumber.length >= 9;
}

/**
 *
 * @param accountNumber validate polish
 * @returns
 */
export function validateAccountNumber(accountNumber: string) {
	const trimmed = accountNumber.trim();
	return /\d{26}/.test(trimmed);
}

/**
 *
 * @param postalCode validate polish
 * @returns
 */
export function validatePostalCode(postalCode: string) {
	const trimmed = postalCode.trim();
	return /^[0-9]{2}-[0-9]{3}/.test(trimmed);
}

export function removeWhiteSpaces(string: string) {
	return string.replace(/\s/g, '');
}

export function isValidDateObj(date: moment.MomentInput) {
	return moment(date).isValid();
}

export function incrementDate(date: moment.MomentInput) {
	return moment(date).add(1, 'day').toDate();
}

export function randomDate(start: Date, end: Date) {
	return new Date(
		start.getTime() + Math.random() * (end.getTime() - start.getTime())
	);
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
	e.returnValue = '';
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const lang = opts?.currency === Currency.EUR ? 'en' : 'pl';

	const formatter = new Intl.NumberFormat(lang, {
		style: 'currency',
		currency: Currency.PLN,
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export function checkIfScreenZoomedIn() {
	return window.devicePixelRatio >= 1.2;
}

export const isApiError = (error: unknown): error is ApiError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as ApiError).message
	);
};

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateForApi(date: DateValue) {
	if (!date) return null;

	return moment(date).format('YYYY-MM-DD');
}

export function getClosestNumber(number: number, array: number[]) {
	return array.reduce(
		(prev, curr) =>
			Math.abs(curr - number) < Math.abs(prev - number) ? curr : prev,
		0
	);
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function getParameterCategoryName(
	translatedName: string,
	unit?: string
) {
	let name = translatedName;
	if (unit) name += ` [${unit}]`;
	return name;
}

export function natSort(nameA = '', nameB = '', order: 'asc' | 'desc' = 'asc') {
	let a: string | number = Number(nameA),
		b: string | number = Number(nameB);

	if (isNaN(a) || isNaN(b))
		return order === 'asc'
			? nameA.localeCompare(nameB)
			: nameB.localeCompare(nameA);

	return order === 'asc' ? a - b : b - a;
}

export function inputWidthValue(value: string) {
	return `${value.length + 3}ch`;
}

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
};

export function getUserStatusColor(status: UserStatus) {
	switch (status) {
		case UserStatus.ACTIVE:
			return 'cyan';
		case UserStatus.INACTIVE:
			return 'gray';
		case UserStatus.SUSPENDED:
			return 'yellow';
	}
}

export function getUserStatusName(status: UserStatus) {
	switch (status) {
		case UserStatus.ACTIVE:
			return 'Aktywny';
		case UserStatus.INACTIVE:
			return 'Nieaktywny';
		case UserStatus.SUSPENDED:
			return 'Zawieszony';
	}
}

export function getComplaintStatusBagdeData(status: ComplaintStatus) {
	switch (status) {
		case ComplaintStatus.NEW:
			return {
				name: 'Nowa',
				color: 'cyan',
			};
		case ComplaintStatus.IN_PROGRESS:
			return {
				name: 'Przetwarzana',
				color: 'yellow',
			};
		case ComplaintStatus.ACCEPTED:
			return {
				name: 'Uznana',
				color: 'lime',
			};
		case ComplaintStatus.REJECTED:
			return {
				name: 'Nieuznana',
				color: 'red',
			};
	}
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1000000 + Number.EPSILON) * 100) / 100;
}

export const getFileCategoryName = (categoryName: string): string => {
	switch (categoryName) {
		case 'Materiały':
			return 'Materiały';
		case 'Marketing':
			return 'Marketing';
		default:
			return categoryName;
	}
};

export const getFileCategoryColor = (categoryName: string): string => {
	switch (categoryName) {
		case 'Materiały':
			return 'blue';
		case 'Marketing':
			return 'green';
		default:
			return 'gray';
	}
};

export function getMimeTypeName(type: AppMimeType) {
	switch (type) {
		case MIME_TYPES.pdf:
			return 'PDF';
		case MIME_TYPES.png:
			return 'PNG';
		case MIME_TYPES.jpeg:
			return 'JPEG';
		default:
			return 'Nieznany typ pliku';
	}
}

export function getOrderStatusBadgeData(status: OrderStatus) {
	switch (status) {
		case OrderStatus.PENDING:
			return {
				name: 'Oczekujące',
				color: 'gray',
			};
		case OrderStatus.PROCESSING:
			return {
				name: 'Przetwarzane',
				color: 'yellow',
			};
		case OrderStatus.COMPLETED:
			return {
				name: 'Zrealizowane',
				color: 'cyan',
			};
		case OrderStatus.CANCELLED:
			return {
				name: 'Anulowane',
				color: 'orange',
			};
	}
}

export function formatConfigValues(
	dimensionsConfig: DimensionsConfig[],
	productDimensions: ProductDimension[]
) {
	const formatted: CreatorState['dimensionConfigValues'] = {};

	for (const config of dimensionsConfig) {
		formatted[config.shortName] = {
			width: config.width?.min || 0,
			height: config.height?.min || 0,
		};
	}

	for (const productDimension of productDimensions) {
		formatted[productDimension.shortName] = {
			width: productDimension.width,
			height: productDimension.height,
		};
	}

	return formatted;
}

export function splitArrayIntoPairs<TData>(arr: TData[]) {
	const result: TData[][] = [];
	for (let i = 0; i < arr.length; i += 2) {
		result.push(arr.slice(i, i + 2));
	}
	return result;
}

export const getDimensionText = (dimension: DimensionObject) => {
	const { width, height } = dimension;
	if (!width && !height) return '-';

	return `${width ? `szerokość: ${width} mm` : ''}${
		width && height ? ', ' : ''
	}${height ? `wysokość ${height} mm` : ''}`;
};

export function isAttributeAnswered(productAttribute: ProductAttribute) {
	switch (productAttribute.attribute.viewType) {
		case AttributeViewType.TILES:
			return !!productAttribute.componentAttributeValue;
		case AttributeViewType.COLOR:
		case AttributeViewType.INPUT:
		case AttributeViewType.THINNING:
			return !!productAttribute.value;

		default:
			return false;
	}
}

export function getDiscountTypeName(discountType: DiscountType) {
	switch (discountType) {
		case DiscountType.FIXED:
			return 'Kwotowy';
		case DiscountType.PERCENTAGE:
			return 'Procentowy';
	}
}
